import React, { useState } from "react";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, MessageSquare, Send, Eye, X } from "lucide-react";

const ROLE_OPTIONS = [
  { key: "", label: "All users" },
  { key: "worker", label: "Workers only" },
  { key: "contractor", label: "Business Partners only" },
  { key: "hr", label: "HR / Recruiters only" },
];

export default function AdminEmailBroadcast() {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [channels, setChannels] = useState({ email: true, whatsapp: false, sms: false });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const runPreview = async () => {
    if (subject.trim().length < 3 || message.trim().length < 3) return toast.error("Subject and message are required");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/broadcast/email", { subject, message, role_filter: roleFilter || undefined, channels: Object.keys(channels).filter((k) => channels[k]), preview_only: true });
      setPreview(data);
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };
  const send = async () => {
    if (!preview) return toast.error("Run Preview first");
    if (!window.confirm(`Send to ${preview.audience_emails} emails and ${preview.audience_mobiles} mobiles?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/broadcast/email", { subject, message, role_filter: roleFilter || undefined, channels: Object.keys(channels).filter((k) => channels[k]) });
      toast.success(`Queued: ${data.email_queued} emails + ${data.wa_sms_queued} WA/SMS`);
      setSubject(""); setMessage(""); setPreview(null);
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6" data-testid="admin-email-broadcast">
      <h1 className="font-display text-3xl font-extrabold sm:text-4xl">Email Campaign</h1>
      <p className="mt-1 text-sm font-medium text-sarthi-muted">Compose once, blast across Email + WhatsApp + SMS. Uses your verified SendGrid sender and Twilio channels.</p>

      <div className="sarthi-card mt-6 p-6">
        <label className="mb-1 block text-xs font-bold uppercase text-sarthi-muted">Subject</label>
        <input data-testid="bc-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. 🎉 New premium jobs this week" className="w-full rounded-xl border-2 border-sarthi-border px-3 py-2 text-base font-semibold" />
        <label className="mt-4 mb-1 block text-xs font-bold uppercase text-sarthi-muted">Message</label>
        <textarea data-testid="bc-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={6} placeholder="Body of the email — line breaks are respected. HTML is sanitized." className="w-full rounded-xl border-2 border-sarthi-border px-3 py-2 text-base" />
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-bold uppercase text-sarthi-muted">Audience</label>
            <select data-testid="bc-role" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="w-full rounded-xl border-2 border-sarthi-border px-3 py-2 font-bold">
              {ROLE_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-bold uppercase text-sarthi-muted">Channels</label>
            <div className="flex flex-wrap gap-2">
              {["email", "whatsapp", "sms"].map((ch) => (
                <label key={ch} data-testid={`bc-ch-${ch}`} className={`inline-flex items-center gap-2 rounded-full border-2 px-3 py-1.5 text-xs font-bold ${channels[ch] ? "border-sarthi-primary bg-sarthi-accent text-sarthi-primary" : "border-sarthi-border bg-white text-sarthi-muted"}`}>
                  <input type="checkbox" checked={!!channels[ch]} onChange={(e) => setChannels({ ...channels, [ch]: e.target.checked })} className="hidden" />
                  {ch === "email" ? <Mail className="h-3 w-3" /> : <MessageSquare className="h-3 w-3" />} {ch}
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          <button data-testid="bc-preview" onClick={runPreview} disabled={busy} className="sarthi-btn-outline inline-flex items-center gap-2 px-4 py-2 text-sm font-bold disabled:opacity-50"><Eye className="h-4 w-4" /> Preview audience</button>
          <button data-testid="bc-send" onClick={send} disabled={busy || !preview} className="sarthi-btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm text-white disabled:opacity-50"><Send className="h-4 w-4" /> Send now</button>
        </div>

        {preview && (
          <div className="mt-6 rounded-xl border-2 border-sarthi-border p-4" data-testid="bc-preview-panel">
            <div className="mb-2 text-sm font-bold text-sarthi-text">Audience</div>
            <div className="text-xs text-sarthi-muted">Emails: <b>{preview.audience_emails}</b> · Mobiles: <b>{preview.audience_mobiles}</b></div>
            <div className="mb-1 mt-3 text-sm font-bold text-sarthi-text">HTML preview</div>
            <div className="max-h-60 overflow-auto rounded-lg border border-sarthi-border/50 bg-white p-3" dangerouslySetInnerHTML={{ __html: preview.preview_html }} />
          </div>
        )}
      </div>
    </div>
  );
}
